import { Drawer, Spin } from 'antd';
import { Profile } from 'oidc-client';
import React from 'react';
import { DrawerWidth } from '../../../config/dimensions';
import { DrawerState } from '../../../core/models/enum';
import { errorWithUserMessage } from '../../../helpers/NotificationHelper';
import { MatchDetailVm, MatchesClient } from '../../../utils/api';
import MatchForm from '../match-form';
import MatchOfficialsPresetForm from './MatchOfficialsPresetForm';

export type MatchSideDrawerOpen = { type: 'officials' | 'details'; matchId: number } | null;

interface Props {
    drawer: MatchSideDrawerOpen;
    onClose: () => void;
    onSaved: () => void | Promise<void>;
    userProfile: Profile;
}

interface State {
    matchDetail?: MatchDetailVm;
    loading: boolean;
}

/**
 * Shared side drawers for match row actions: officials (preset form) and match details/edit
 * (same MatchForm as the match details page Edit drawer). Lineups are edited only in the match log.
 */
class MatchSideDrawers extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            loading: false,
        };
    }

    public componentDidMount = (): void => {
        const { drawer } = this.props;
        if (drawer) {
            void this.loadMatch(drawer.matchId);
        }
    };

    public componentDidUpdate = (prevProps: Props): void => {
        const { drawer } = this.props;
        if (drawer && (!prevProps.drawer || prevProps.drawer.matchId !== drawer.matchId)) {
            void this.loadMatch(drawer.matchId);
        }
        if (!drawer && prevProps.drawer) {
            this.setState({ matchDetail: undefined });
        }
    };

    private loadMatch = async (matchId: number): Promise<void> => {
        // Drop previous match immediately so we never show MatchForm with stale matchFromProps
        // while fetching another id (Ant Form initialValue only applies on first mount).
        this.setState({ loading: true, matchDetail: undefined });
        try {
            const detail = await new MatchesClient().getById(matchId);
            this.setState({ matchDetail: detail, loading: false });
        } catch {
            this.setState({ loading: false, matchDetail: undefined });
            errorWithUserMessage('Could not load match.');
        }
    };

    private handleSaved = async (): Promise<void> => {
        const { onSaved, drawer } = this.props;
        if (drawer) {
            await this.loadMatch(drawer.matchId);
        }
        await onSaved();
    };

    /** Reload match + parent table, then close drawer (same UX as Details save). */
    private handleSavedAndCloseDrawer = async (): Promise<void> => {
        const { onClose } = this.props;
        await this.handleSaved();
        onClose();
    };

    public render(): React.ReactElement {
        const { drawer, onClose, userProfile, onSaved } = this.props;
        const { matchDetail, loading } = this.state;

        const visible = !!drawer;
        const title = drawer?.type === 'officials' ? 'Match officials' : 'Edit match';

        return (
            <Drawer
                title={title}
                visible={visible}
                onClose={onClose}
                width={DrawerWidth}
                destroyOnClose
            >
                <Spin spinning={loading || !matchDetail}>
                    {visible && matchDetail && drawer?.type === 'officials' && (
                        <MatchOfficialsPresetForm
                            key={matchDetail.id}
                            matchDetail={matchDetail}
                            onSaved={this.handleSavedAndCloseDrawer}
                        />
                    )}
                    {visible && matchDetail && drawer?.type === 'details' && (
                        <MatchForm
                            key={matchDetail.id}
                            handleClose={(_mode: DrawerState, _id?: string): void => {
                                onClose();
                            }}
                            refreshAfterSave={(): void => {
                                void this.handleSaved();
                            }}
                            matchFromProps={matchDetail}
                            showDeleteInDrawer
                            userProfile={userProfile}
                            onDeleted={async (): Promise<void> => {
                                await onSaved();
                            }}
                        />
                    )}
                </Spin>
            </Drawer>
        );
    }
}

export default MatchSideDrawers;
